// Notifications — which tracking news go to the recipient by SMS, and the
// text of each. Off by default; one SMS per parcel and notification; texts
// default per language and may use {tracking}, {carrier}, {order}.

import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPen, FaUndo } from 'react-icons/fa';
import { EVENTS, renderSms, smsLength } from './api';

const VARIABLE_LABELS = { tracking: 'N° de suivi', carrier: 'Transporteur', order: 'N° de commande' };

const LengthMeter = ({ text, t }) => {
    const { unicode, chars, parts } = smsLength(text);
    const limit = unicode ? (parts > 1 ? 67 * parts : 70) : (parts > 1 ? 153 * parts : 160);
    return (
        <span className={`csms-length ${parts > 1 ? 'csms-length--multi' : ''}`}>
            <span className="csms-length-bar" aria-hidden="true"><span style={{ width: `${Math.min(100, (chars / limit) * 100)}%` }} /></span>
            {t('customerSms.events.length', '{{chars}} caractères · {{parts}} SMS', { chars, parts })}
            {unicode && <em>{t('customerSms.events.unicode', ' · caractères spéciaux : 70 par SMS')}</em>}
        </span>
    );
};

const EventRow = ({ event, config, language, samples, canEdit, focused, onFocus, onChange, problemOn, t }) => {
    const [editing, setEditing] = useState(Boolean(config.text));
    const textRef = useRef(null);
    const defaultText = config.default_text?.[language] || '';
    const text = config.text ?? '';
    const effective = text.trim() ? text : defaultText;

    const insert = (name) => {
        const token = `{${name}}`;
        const el = textRef.current;
        const base = text || defaultText;
        if (!el) { onChange({ text: `${base} ${token}` }); return; }
        const start = el.selectionStart ?? base.length;
        const end = el.selectionEnd ?? base.length;
        onChange({ text: `${base.slice(0, start)}${token}${base.slice(end)}` });
        requestAnimationFrame(() => { el.focus(); el.setSelectionRange(start + token.length, start + token.length); });
    };

    return (
        <li className={`csms-event ${focused ? 'csms-event--focused' : ''} ${config.enabled ? 'csms-event--on' : ''}`}
            onFocus={onFocus} onMouseEnter={onFocus}>
            <div className="csms-event-head">
                <button type="button" role="switch" aria-checked={config.enabled} disabled={!canEdit}
                    className={`csms-switch ${config.enabled ? 'csms-switch--on' : ''}`}
                    onClick={() => onChange({ enabled: !config.enabled })}
                    aria-label={t(`customerSms.events.${event}.title`, event)}>
                    <span className="csms-switch-knob" />
                </button>
                <div className="csms-event-title">
                    <strong>{t(`customerSms.events.${event}.title`, event)}</strong>
                    <small>{t(`customerSms.events.${event}.when`, '')}</small>
                </div>
                {canEdit && !editing && (
                    <button type="button" className="csms-link" onClick={() => setEditing(true)}>
                        <FaPen /> {t('customerSms.events.edit', 'Personnaliser le texte')}
                    </button>
                )}
            </div>

            {config.enabled && !problemOn
                && (event === 'parcel_moving_again' || (event === 'parcel_delivered' && config.only_after_problem !== false)) && (
                <p className="csms-dependency" role="status">
                    {t('customerSms.events.needsProblem', 'Ne part qu’après un SMS « Colis en retard », qui est désactivé : activez-le aussi.')}
                </p>
            )}

            {event === 'parcel_delivered' && config.enabled && (
                <div className="csms-scope" role="radiogroup" aria-label={t('customerSms.events.scope', 'Pour quels colis')}>
                    {[true, false].map((only) => (
                        <label key={String(only)} className="csms-scope-option">
                            <input type="radio" name="csms-delivered-scope" checked={config.only_after_problem === only}
                                disabled={!canEdit} onChange={() => onChange({ only_after_problem: only })} />
                            <span>{only
                                ? t('customerSms.events.onlyAfterProblem', 'Seulement si un retard a été annoncé (recommandé)')
                                : t('customerSms.events.everyDelivery', 'Pour chaque colis livré')}</span>
                        </label>
                    ))}
                </div>
            )}

            {editing ? (
                <div className="csms-editor">
                    <textarea ref={textRef} rows={3} value={text || defaultText} disabled={!canEdit}
                        maxLength={300} onChange={(e) => onChange({ text: e.target.value })} />
                    <div className="csms-editor-bar">
                        <span className="csms-vars">
                            {Object.keys(VARIABLE_LABELS).map((name) => (
                                <button key={name} type="button" className="csms-var" disabled={!canEdit} onClick={() => insert(name)}
                                    title={`{${name}}`}>
                                    + {t(`customerSms.variables.${name}`, VARIABLE_LABELS[name])}
                                </button>
                            ))}
                        </span>
                        <LengthMeter text={renderSms(effective, samples)} t={t} />
                    </div>
                    {canEdit && text && text !== defaultText && (
                        <button type="button" className="csms-link" onClick={() => onChange({ text: null })}>
                            <FaUndo /> {t('customerSms.events.reset', 'Revenir au texte par défaut')}
                        </button>
                    )}
                </div>
            ) : (
                <p className="csms-event-text">{renderSms(effective, samples)}</p>
            )}
        </li>
    );
};

const NotificationsSection = ({ view, draft, onDraft, focusEvent, setFocusEvent }) => {
    const { t } = useTranslation();
    const canEdit = view.can_edit;
    const [from, until] = view.limits?.sending_hours || [8, 20];

    const setEvent = (event, patch) => onDraft({
        events: { ...draft.events, [event]: { ...draft.events[event], ...patch } },
    });

    return (
        <section className="csms-card" aria-labelledby="csms-events-title">
            <header className="csms-card-header">
                <div>
                    <h2 id="csms-events-title">{t('customerSms.events.title', 'Notifications envoyées')}</h2>
                    <p>{t('customerSms.events.subtitle', 'Au destinataire du colis, depuis le numéro de mobile de la commande.')}</p>
                </div>
                <label className="csms-field csms-field--inline">
                    <span>{t('customerSms.language', 'Langue')}</span>
                    <select value={draft.language} disabled={!canEdit} onChange={(e) => onDraft({ language: e.target.value })}>
                        <option value="fr">Français</option>
                        <option value="en">English</option>
                    </select>
                </label>
            </header>
            <ul className="csms-events">
                {EVENTS.map((event) => (
                    <EventRow key={event} event={event} config={{ ...view.events[event], ...draft.events[event] }}
                        language={draft.language} samples={view.samples} canEdit={canEdit}
                        focused={focusEvent === event} onFocus={() => setFocusEvent(event)}
                        onChange={(patch) => setEvent(event, patch)} problemOn={draft.events.parcel_problem.enabled} t={t} />
                ))}
            </ul>
            <ul className="csms-rules">
                <li>{t('customerSms.rules.once', 'Un SMS au plus par colis et par notification.')}</li>
                <li>{t('customerSms.rules.hours', 'Envoyés entre {{from}} h et {{until}} h (heure de Paris) ; la nuit, ils partent à {{from}} h.', { from, until })}</li>
                <li>{t('customerSms.rules.mobile', "Seuls les numéros de mobile valides reçoivent un SMS ; l'indicatif est déduit du pays de livraison.")}</li>
            </ul>
        </section>
    );
};

export default NotificationsSection;
